import {
    LanguageModelV1,
    LanguageModelV1CallWarning,
    LanguageModelV1FinishReason,
    LanguageModelV1StreamPart,
} from '@ai-sdk/provider';
import {
    combineHeaders,
    createEventSourceResponseHandler,
    createJsonResponseHandler,
    FetchFunction,
    ParseResult,
    postJsonToApi,
} from '@ai-sdk/provider-utils';
import {z} from 'zod';
import {convertToDifyChatMessages, convertToDifyQuery} from './convert-to-dify-chat-messages';
import {mapDifyFinishReason} from './map-dify-finish-reason';
import {DifyChatModelId, DifyChatSettings,} from './dify-chat-settings';
import {difyFailedResponseHandler} from './dify-error';
import {getResponseMetadata} from './get-response-metadata';
import {prepareTools} from './dify-prepare-tools';
import {generateMarkdown} from "./dify-citation-render";

type DifyChatConfig = {
    provider: string;
    baseURL: string;
    headers: () => Record<string, string | undefined>;
    fetch?: FetchFunction;
};

export class DifyChatLanguageModel implements LanguageModelV1 {
    readonly specificationVersion = 'v1';
    readonly defaultObjectGenerationMode = 'json';
    readonly supportsImageUrls = false;

    readonly modelId: DifyChatModelId;
    readonly settings: DifyChatSettings;

    private readonly config: DifyChatConfig;

    constructor(
        modelId: DifyChatModelId,
        settings: DifyChatSettings,
        config: DifyChatConfig,
    ) {
        this.modelId = modelId;
        this.settings = settings;
        this.config = config;
    }

    get provider(): string {
        return this.config.provider;
    }

    private getArgs({
                        mode,
                        prompt,
                        maxTokens,
                        temperature,
                        topP,
                        topK,
                        frequencyPenalty,
                        presencePenalty,
                        stopSequences,
                        responseFormat,
                        seed,
                    }: Parameters<LanguageModelV1['doGenerate']>[0]) {
        const type = mode.type;

        const warnings: LanguageModelV1CallWarning[] = [];

        if (topK != null) {
            warnings.push({
                type: 'unsupported-setting',
                setting: 'topK',
            });
        }

        if (frequencyPenalty != null) {
            warnings.push({
                type: 'unsupported-setting',
                setting: 'frequencyPenalty',
            });
        }

        if (presencePenalty != null) {
            warnings.push({
                type: 'unsupported-setting',
                setting: 'presencePenalty',
            });
        }

        if (stopSequences != null) {
            warnings.push({
                type: 'unsupported-setting',
                setting: 'stopSequences',
            });
        }

        if (
            responseFormat != null &&
            responseFormat.type === 'json' &&
            responseFormat.schema != null
        ) {
            warnings.push({
                type: 'unsupported-setting',
                setting: 'responseFormat',
                details: 'JSON response format schema is not supported',
            });
        }

        const baseArgs = {
            // model id:
            model: this.modelId,
            user: this.settings.userId,
            conversation_id: this.settings.conversationId,


            // model specific settings:
            // safe_prompt: this.settings.safePrompt,

            // standardized settings:
            max_tokens: maxTokens,
            temperature,
            top_p: topP,
            random_seed: seed,

            // response format:
            response_format:
                responseFormat?.type === 'json' ? {type: 'json_object'} : undefined,

            // messages:
            messages: convertToDifyChatMessages(prompt),
            query: convertToDifyQuery(prompt),
            inputs: {
                tenant_id: this.settings.tenantId,
            },
        };

        switch (type) {
            case 'regular': {
                const {tools, tool_choice, toolWarnings} = prepareTools(mode);

                return {
                    args: {...baseArgs, tools, tool_choice},
                    warnings: [...warnings, ...toolWarnings],
                };
            }

            case 'object-json': {
                return {
                    args: {
                        ...baseArgs,
                        response_format: {type: 'json_object'},
                    },
                    warnings,
                };
            }

            case 'object-tool': {
                return {
                    args: {
                        ...baseArgs,
                        tool_choice: 'any',
                        tools: [{type: 'function', function: mode.tool}],
                    },
                    warnings,
                };
            }

            default: {
                const _exhaustiveCheck: never = type;
                throw new Error(`Unsupported type: ${_exhaustiveCheck}`);
            }
        }
    }

    async doGenerate(
        options: Parameters<LanguageModelV1['doGenerate']>[0],
    ): Promise<Awaited<ReturnType<LanguageModelV1['doGenerate']>>> {
        const {args, warnings} = this.getArgs(options);

        const {responseHeaders, value: response} = await postJsonToApi({
            url: `${this.config.baseURL}/chat/completions`,
            headers: combineHeaders(this.config.headers(), options.headers),
            body: args,
            failedResponseHandler: difyFailedResponseHandler,
            successfulResponseHandler: createJsonResponseHandler(
                mistralChatResponseSchema,
            ),
            abortSignal: options.abortSignal,
            fetch: this.config.fetch,
        });

        const {messages: rawPrompt, ...rawSettings} = args;
        const choice = response.choices[0];
        let text = choice.message.content ?? undefined;

        // when there is a trailing assistant message, mistral will send the
        // content of that message again. we skip this repeated content to
        // avoid duplication, e.g. in continuation mode.
        const lastMessage = rawPrompt[rawPrompt.length - 1];
        if (
            lastMessage.role === 'assistant' &&
            text?.startsWith(lastMessage.content)
        ) {
            text = text.slice(lastMessage.content.length);
        }

        return {
            text,
            toolCalls: choice.message.tool_calls?.map(toolCall => ({
                toolCallType: 'function',
                toolCallId: toolCall.id,
                toolName: toolCall.function.name,
                args: toolCall.function.arguments!,
            })),
            finishReason: mapDifyFinishReason(choice.finish_reason),
            usage: {
                promptTokens: response.usage.prompt_tokens,
                completionTokens: response.usage.completion_tokens,
            },
            rawCall: {rawPrompt, rawSettings},
            rawResponse: {headers: responseHeaders},
            request: {body: JSON.stringify(args)},
            response: getResponseMetadata(response),
            warnings,
        };
    }

    async doStream(
        options: Parameters<LanguageModelV1['doStream']>[0],
    ): Promise<Awaited<ReturnType<LanguageModelV1['doStream']>>> {
        const {args, warnings} = this.getArgs(options);
        const settings = this.settings

        const body = {
            ...args,
            response_mode: "streaming",
            auto_generate_name: true,
        };

        const {responseHeaders, value: response} = await postJsonToApi({
            url: `${this.config.baseURL}/chat-messages`,
            headers: combineHeaders(this.config.headers(), options.headers),
            body,
            failedResponseHandler: difyFailedResponseHandler,
            successfulResponseHandler: createEventSourceResponseHandler(
                difyChatChunkSchema,
            ),
            abortSignal: options.abortSignal,
            fetch: this.config.fetch,
        });

        const {messages: rawPrompt, ...rawSettings} = args;

        let finishReason: LanguageModelV1FinishReason = 'unknown';
        let usage: { promptTokens: number; completionTokens: number } = {
            promptTokens: Number.NaN,
            completionTokens: Number.NaN,
        };

        return {
            stream: response.pipeThrough(
                new TransformStream<
                    ParseResult<z.infer<typeof difyChatChunkSchema>>,
                    LanguageModelV1StreamPart
                >({
                    async transform(chunk, controller) {
                        if (!chunk.success) {
                            controller.enqueue({type: 'error', error: chunk.error});
                            return;
                        }

                        // console.log('chunkNumber', chunkNumber, 'chunk', chunk)
                        const value = chunk.value;
                        switch (value.event) {
                            case "message": {
                                controller.enqueue({
                                    type: 'text-delta',
                                    textDelta: value.answer as string,
                                });
                                break
                            }
                            case "message_end": {
                                settings.setConversationId?.(value.conversation_id as string)
                                console.log('value', value)
                                const textDelta = await generateMarkdown(value)
                                controller.enqueue({
                                    type: 'text-delta',
                                    textDelta: textDelta
                                });
//                                 controller.enqueue({
//                                     type: 'text-delta',
//                                     textDelta: `
//
// ---
//
// > 參考連結
// ${value.metadata?.retriever_resources?.map(resource => {
//                                         return ` [${resource.document_name}](${resource.document_name})`
//                                     })}
// `
//                                 });
                                break
                            }
                            default: {
                            }
                        }
                        return
                        // const value = chunk.value;
                        //
                        // if (chunkNumber === 1) {
                        //     controller.enqueue({
                        //         type: 'response-metadata',
                        //         ...getResponseMetadata(value),
                        //     });
                        // }
                        //
                        // if (value.usage != null) {
                        //     usage = {
                        //         promptTokens: value.usage.prompt_tokens,
                        //         completionTokens: value.usage.completion_tokens,
                        //     };
                        // }
                        //
                        // const choice = value.choices[0];
                        //
                        // if (choice?.finish_reason != null) {
                        //     finishReason = mapDifyFinishReason(choice.finish_reason);
                        // }
                        //
                        // if (choice?.delta == null) {
                        //     return;
                        // }
                        //
                        // const delta = choice.delta;
                        //
                        // // when there is a trailing assistant message, mistral will send the
                        // // content of that message again. we skip this repeated content to
                        // // avoid duplication, e.g. in continuation mode.
                        // if (chunkNumber <= 2) {
                        //     const lastMessage = rawPrompt[rawPrompt.length - 1];
                        //
                        //     if (
                        //         lastMessage.role === 'assistant' &&
                        //         delta.content === lastMessage.content.trimEnd()
                        //     ) {
                        //         // Mistral moves the trailing space from the prefix to the next chunk.
                        //         // We trim the leading space to avoid duplication.
                        //         if (delta.content.length < lastMessage.content.length) {
                        //             trimLeadingSpace = true;
                        //         }
                        //
                        //         // skip the repeated content:
                        //         return;
                        //     }
                        // }
                        //
                        // if (delta.content != null) {
                        //     controller.enqueue({
                        //         type: 'text-delta',
                        //         textDelta: trimLeadingSpace
                        //             ? delta.content.trimStart()
                        //             : delta.content,
                        //     });
                        //
                        //     trimLeadingSpace = false;
                        // }
                        //
                        // if (delta.tool_calls != null) {
                        //     for (const toolCall of delta.tool_calls) {
                        //         // mistral tool calls come in one piece:
                        //         controller.enqueue({
                        //             type: 'tool-call-delta',
                        //             toolCallType: 'function',
                        //             toolCallId: toolCall.id,
                        //             toolName: toolCall.function.name,
                        //             argsTextDelta: toolCall.function.arguments,
                        //         });
                        //         controller.enqueue({
                        //             type: 'tool-call',
                        //             toolCallType: 'function',
                        //             toolCallId: toolCall.id,
                        //             toolName: toolCall.function.name,
                        //             args: toolCall.function.arguments,
                        //         });
                        //     }
                        // }
                    },

                    flush(controller) {
                        controller.enqueue({type: 'finish', finishReason, usage});
                    },
                }),
            ),
            rawCall: {rawPrompt, rawSettings},
            rawResponse: {headers: responseHeaders},
            request: {body: JSON.stringify(body)},
            warnings,
        };
    }
}

// limited version of the schema, focussed on what is needed for the implementation
// this approach limits breakages when the API changes and increases efficiency
const mistralChatResponseSchema = z.object({
    id: z.string().nullish(),
    created: z.number().nullish(),
    model: z.string().nullish(),
    choices: z.array(
        z.object({
            message: z.object({
                role: z.literal('assistant'),
                content: z.string().nullable(),
                tool_calls: z
                    .array(
                        z.object({
                            id: z.string(),
                            function: z.object({name: z.string(), arguments: z.string()}),
                        }),
                    )
                    .nullish(),
            }),
            index: z.number(),
            finish_reason: z.string().nullish(),
        }),
    ),
    object: z.literal('chat.completion'),
    usage: z.object({
        prompt_tokens: z.number(),
        completion_tokens: z.number(),
    }),
});

// limited version of the schema, focussed on what is needed for the implementation
// this approach limits breakages when the API changes and increases efficiency
export const difyChatChunkRetrieverResource = z.object({
    position: z.number().optional(),
    dataset_id: z.string().optional(),
    dataset_name: z.string().optional(),
    document_id: z.string().optional(),
    document_name: z.string().optional(),
    segment_id: z.string().optional(),
    score: z.number().optional(),
    content: z.string().optional(),
});
export const difyChatChunkSchema = z.object({
    event: z.string(),
    task_id: z.string().optional(),
    message_id: z.string().optional(),
    conversation_id: z.string().optional(),
    answer: z.string().optional(),
    created_at: z.number().optional(),
    id: z.string().optional(),
    type: z.string().optional(),
    belongs_to: z.string().optional(),
    url: z.string().optional(),
    metadata: z.object({
        usage: z.object({
            prompt_tokens: z.number().optional(),
            prompt_unit_price: z.string().optional(),
            prompt_price_unit: z.string().optional(),
            prompt_price: z.string().optional(),
            completion_tokens: z.number().optional(),
            completion_unit_price: z.string().optional(),
            completion_price_unit: z.string().optional(),
            completion_price: z.string().optional(),
            total_tokens: z.number().optional(),
            total_price: z.string().optional(),
            currency: z.string().optional(),
            latency: z.number().optional(),
        }).optional(),
        retriever_resources: z.array(difyChatChunkRetrieverResource).optional(),
    }).optional(),
    usage: z.object({}).optional(),
    retriever_resources: z.array(z.object({})).optional(),
    audio: z.string().optional(),
    workflow_run_id: z.string().optional(),
    workflow_id: z.string().optional(),
    sequence_number: z.number().optional(),
    node_id: z.string().optional(),
    node_type: z.string().optional(),
    title: z.string().optional(),
    index: z.number().optional(),
    predecessor_node_id: z.string().optional(),
    inputs: z.array(z.object({})).optional(),
    process_data: z.object({}).optional(),
    outputs: z.object({}).optional(),
    status: z.string().optional(),
    error: z.string().optional(),
    elapsed_time: z.number().optional(),
    execution_metadata: z.object({}).optional(),
    total_tokens: z.number().optional(),
    total_price: z.number().optional(),
    currency: z.string().optional(),
    finished_at: z.number().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
});
// const difyChatChunkSchema = z.object({
//     id: z.string().nullish(),
//     created: z.number().nullish(),
//     model: z.string().nullish(),
//     choices: z.array(
//         z.object({
//             delta: z.object({
//                 role: z.enum(['assistant']).optional(),
//                 content: z.string().nullish(),
//                 tool_calls: z
//                     .array(
//                         z.object({
//                             id: z.string(),
//                             function: z.object({name: z.string(), arguments: z.string()}),
//                         }),
//                     )
//                     .nullish(),
//             }),
//             finish_reason: z.string().nullish(),
//             index: z.number(),
//         }),
//     ),
//     usage: z
//         .object({
//             prompt_tokens: z.number(),
//             completion_tokens: z.number(),
//         })
//         .nullish(),
// });
